/**
 * Le **porteur** du document ouvert dans le canvas : la conversation à qui ce
 * document appartient.
 *
 * Avant ce module (#862), cette identité existait en cinq exemplaires dérivés —
 * une propriété React descendue de l'écran assistant, une copie dans les
 * métadonnées de gabarit, une copie persistée dans le stockage local, une copie
 * figée dans la session du coordinateur de documents, et une résolution par
 * comparaison côté transport temps réel. Chacune se perdait ou se
 * désynchronisait à un endroit différent : c'est la cause commune des sept
 * écarts recensés par la carte #823.
 *
 * La règle de détermination vit ICI, et nulle part ailleurs. Le résultat est
 * publié dans `canvasEditorStore.holderConversationId`, que tout le reste lit —
 * y compris hors de React (transport temps réel, coordinateur de documents),
 * qui n'ont donc plus à deviner.
 *
 * **Deux grandeurs distinctes, à ne jamais confondre** :
 * - la *conversation affichée* — ce que l'avocat regarde, dicté par l'URL ;
 * - le *porteur* — à qui appartient le document ouvert.
 *
 * Elles ont coïncidé tant que le canvas vivait dans l'écran assistant. **Elles
 * divergent depuis #856** : le canvas réside dans la coque, l'avocat peut aller
 * consulter un dossier, et le document continue d'appartenir à sa conversation
 * pendant qu'aucune n'est affichée. C'est précisément pour cela qu'elles
 * portaient déjà deux noms avant d'en avoir besoin.
 */

/**
 * Le gabarit chargé appartient-il à la conversation qui porte le canvas ?
 *
 * Un gabarit **sans** conversation n'appartient à personne en particulier : il
 * suit le canvas. C'est le cas d'une page blanche et d'un brouillon rechargé par
 * « Copier un brouillon d’une autre conversation » — ce dernier est une *copie* déposée dans le
 * canvas courant, pas le brouillon d'origine, qui lui reste dans sa
 * conversation.
 *
 * Répondre `false` neutralise le gabarit (`RedactionPanel`) : il cesse d'être
 * lu et son blob est purgé. Sans cette garde, un Word ouvert dans la
 * conversation A s'affiche en silence dans la conversation B jusqu'à ce qu'un
 * autre document soit chargé.
 */
export function isTemplateHeldByCanvasHolder(
  templateConversationId: string | null | undefined,
  holderConversationId: string | null | undefined,
): boolean {
  if (!templateConversationId) return true;
  return templateConversationId === holderConversationId;
}

/**
 * Le canvas tient-il quelque chose qu'on regretterait de perdre de vue ?
 *
 * Trois signes, et il suffit d'un : un gabarit chargé, un brouillon
 * enregistré, ou des frappes non encore enregistrées. Aucun ne dépend d'un
 * composant monté — c'est tout l'objet de #856.
 *
 * Sert à décider si arriver sur un assistant **sans conversation** (« Nouvelle
 * conversation », retour sur `/assistant` sans `?c=`) doit refermer le canvas.
 * La règle d'origine le refermait toujours, pour qu'un fil neuf n'hérite pas
 * d'un panneau ouvert. Depuis que le document survit à la navigation, appliquée
 * telle quelle elle referme sous les yeux de l'avocat le document qu'on vient
 * de préserver. Un canvas vide, lui, n'a rien à défendre.
 */
export function canvasHoldsDocument(canvas: {
  templateOverride: unknown;
  hasDraft: boolean;
  hasUnsavedChanges: boolean;
}): boolean {
  return canvas.templateOverride !== null || canvas.hasDraft || canvas.hasUnsavedChanges;
}

/**
 * Le libellé d'attente d'un fil optimiste, pas un nom. Ce sont exactement les
 * mots du bouton qui ouvre un fil vierge : rendus tels quels dans la pastille,
 * ils diraient à l'avocat que le document appartient à l'écran vide qu'il
 * regarde — la confusion même que #853 supprime.
 */
const LIBELLE_FIL_SANS_NOM = 'Nouvelle conversation';

/**
 * Le titre d'une conversation **tel qu'on a le droit de le montrer**, ou `null`.
 *
 * Un titre vide et le libellé d'attente d'un fil optimiste sont traités
 * pareillement : ce ne sont pas des noms. La règle vit ici, et non dupliquée
 * chez chaque afficheur — les projections de résidence la réutilisent, où citer
 * « Nouvelle conversation » à l'avocat ne désignerait rien.
 */
export function nomAffichableDeConversation(titre: string | null | undefined): string | null {
  const propre = titre?.trim();
  if (!propre || propre === LIBELLE_FIL_SANS_NOM) return null;
  return propre;
}

/**
 * **Le porteur est une valeur RETENUE, pas une valeur calculée** — aucune
 * fonction ne le *détermine* ici, et il ne faut pas en écrire une.
 *
 * Il n'y a plus non plus de fonction qui le *montre* : la pastille qui le
 * nommait à l'écran a été retirée par #1333, avec
 * `resolveCanvasHolderConversation`. Elle promettait de désigner « une AUTRE
 * conversation », or on ne voit jamais le canvas d'autrui depuis #1281 — elle
 * répétait donc la barre latérale. Le signal « rédaction ailleurs » reste dans
 * `RedactionEnCoursBanner` (#859).
 *
 * Il ne suit PAS l'écran : depuis #1331, la scène (`canvasStage`) ne l'écrit
 * plus. Ses seuls écrivains sont la résidence du canvas, qui le pose sur le
 * document qu'elle rend visible (`CanvasResidence`), et l'éviction au
 * changement de compte (`canvasEviction`). Quitter l'assistant ne le touche
 * pas : le document garde son propriétaire pendant qu'on regarde ailleurs (#856).
 *
 * L'intérêt du porteur n'est donc pas d'être calculé — il est d'être **publié à
 * un seul endroit** au lieu d'être redérivé partout, dont deux fois hors de
 * React où la valeur se figeait au montage (#862).
 *
 * Le porteur est une projection visible, jamais une autorité : la capacité est
 * gérée par les résidences et l'activité par les compteurs de session exacte.
 */
